/**
 * @fileoverview RFQ Access Restricted Component
 * 
 * Displays a blocked access page for unverified vendors trying to view RFQ details.
 * Provides clear messaging about verification requirements and a CTA to get verified.
 */

import { ShieldAlert, ArrowLeft, CheckCircle, Star, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Link } from 'react-router-dom';

export function RFQAccessRestricted() {
  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-2xl">
      <Link to="/rfqs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to RFQs
      </Link>

      <Card className="border-2 border-orange-200 bg-orange-50/50">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center">
            <ShieldAlert className="h-8 w-8 text-orange-600" />
          </div>
          <CardTitle className="text-2xl">Verification Required</CardTitle>
          <CardDescription className="text-base">
            Your profile must be verified to view RFQ details and submit bids.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Benefits Section */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Why Get Verified?
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Access detailed RFQ specifications and requirements</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Submit competitive bids to clients</span>
              </li>
              <li className="flex items-start gap-3">
                <Star className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <span>Build trust with potential clients</span>
              </li>
              <li className="flex items-start gap-3">
                <Award className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>Stand out from unverified vendors</span>
              </li>
            </ul>
          </div>

          {/* Verification Steps */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-3">
              How to Get Verified
            </h3>
            <ol className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="font-semibold text-primary">1.</span>
                <span>Complete your vendor profile with business information</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-primary">2.</span>
                <span>Add relevant certifications and capabilities</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-primary">3.</span>
                <span>Request verification from your profile page</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold text-primary">4.</span>
                <span>Our team will review and approve your profile</span>
              </li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button asChild className="flex-1">
              <Link to="/profile/vendor">Request Verification</Link>
            </Button>
            <Button variant="outline" asChild className="flex-1">
              <Link to="/rfqs">Browse RFQs</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
